import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent, type WheelEvent } from 'react';
import { ArrowLeft, Check, Eraser, Grid3X3, ImagePlus, LoaderCircle, Maximize2, Move, Redo2, ScanLine, ShieldCheck, SwatchBook, Undo2, WandSparkles, X, ZoomIn, ZoomOut } from 'lucide-react';
import { MARD_291, hexToRgb, readableTextColor } from '../core/palette';
import { maskRecognitionCells, matchRecognitionGroupsToPalette, materializeRecognition, recognizePatternImage } from '../core/recognizePattern';
import type { PatternGridBounds, PatternRecognitionDraft, ProcessResult } from '../types';
import { DimensionInput } from './DimensionInput';
import { ShortcutPaletteModal } from './ShortcutPaletteModal';

interface Props {
  file: File;
  maxCanvasSize: number;
  shortcutColors: number[];
  onCancel: () => void;
  onConfirm: (result: ProcessResult, shortcutColors: number[]) => void;
}

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';
interface PreviewView { zoom: number; x: number; y: number }

function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

function colorDistance(left: string, right: string) {
  const a = hexToRgb(left);
  const b = hexToRgb(right);
  const redMean = (a.r + b.r) / 2;
  const red = a.r - b.r;
  const green = a.g - b.g;
  const blue = a.b - b.b;
  return Math.sqrt((2 + redMean / 256) * red * red + 4 * green * green + (2 + (255 - redMean) / 256) * blue * blue);
}

function fitBounds(width: number, height: number, natural: { width: number; height: number }): PatternGridBounds {
  if (natural.width <= 1 || natural.height <= 1) return { left: 0.03, top: 0.03, right: 0.97, bottom: 0.97 };
  const left = 0.03;
  const top = 0.03;
  let boardWidth = natural.width * 0.94;
  let boardHeight = boardWidth * height / width;
  const availableHeight = natural.height * 0.95;
  if (boardHeight > availableHeight) {
    boardHeight = availableHeight;
    boardWidth = boardHeight * width / height;
  }
  return {
    left,
    top,
    right: clamp(left + boardWidth / natural.width, left + 0.1, 0.99),
    bottom: clamp(top + boardHeight / natural.height, top + 0.1, 0.99)
  };
}

function rulerMarkers(count: number) {
  const markers: number[] = [];
  for (let value = 10; value <= count; value += 10) markers.push(value);
  if (markers.at(-1) !== count) markers.push(count);
  return markers;
}

function DraftPreview({ draft, mappings }: { draft: PatternRecognitionDraft; mappings: Map<number, number> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = draft.width;
    canvas.height = draft.height;
    const context = canvas.getContext('2d');
    if (!context) return;
    const image = context.createImageData(draft.width, draft.height);
    for (let index = 0; index < draft.groupIndices.length; index += 1) {
      const pixel = index * 4;
      if (draft.empty[index]) {
        image.data[pixel + 3] = 0;
        continue;
      }
      const paletteIndex = mappings.get(draft.groupIndices[index]);
      if (paletteIndex === undefined || paletteIndex < 0) {
        image.data[pixel + 3] = 0;
        continue;
      }
      const color = hexToRgb(MARD_291[paletteIndex]?.hex || '#ffffff');
      image.data[pixel] = color.r;
      image.data[pixel + 1] = color.g;
      image.data[pixel + 2] = color.b;
      image.data[pixel + 3] = 255;
    }
    context.putImageData(image, 0, 0);
  }, [draft, mappings]);
  return <canvas ref={canvasRef} className="recognition-result-canvas" aria-label="识别结果预览" />;
}

function CalibrationMagnifier({ image, bounds, columns, rows, point }: { image: HTMLImageElement | null; bounds: PatternGridBounds; columns: number; rows: number; point: { x: number; y: number } | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const canvasWidth = 600;
    const canvasHeight = 400;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    context.clearRect(0, 0, canvasWidth, canvasHeight);
    context.fillStyle = '#eee9e2';
    context.fillRect(0, 0, canvasWidth, canvasHeight);
    if (!image || !point || !image.naturalWidth || !image.naturalHeight) return;

    const cellWidth = (bounds.right - bounds.left) / Math.max(1, columns);
    const cropWidth = 0.045;
    const cropHeight = Math.min(1, cropWidth * image.naturalWidth / image.naturalHeight * canvasHeight / canvasWidth);
    const cropLeft = clamp(point.x - cropWidth / 2, 0, 1 - cropWidth);
    const cropTop = clamp(point.y - cropHeight / 2, 0, 1 - cropHeight);
    context.imageSmoothingEnabled = true;
    context.drawImage(
      image,
      cropLeft * image.naturalWidth,
      cropTop * image.naturalHeight,
      cropWidth * image.naturalWidth,
      cropHeight * image.naturalHeight,
      0,
      0,
      canvasWidth,
      canvasHeight
    );

    const cellHeight = (bounds.bottom - bounds.top) / Math.max(1, rows);
    const toCanvasX = (value: number) => (value - cropLeft) / cropWidth * canvasWidth;
    const toCanvasY = (value: number) => (value - cropTop) / cropHeight * canvasHeight;
    const firstColumn = Math.max(0, Math.floor((cropLeft - bounds.left) / cellWidth) - 1);
    const lastColumn = Math.min(columns, Math.ceil((cropLeft + cropWidth - bounds.left) / cellWidth) + 1);
    const firstRow = Math.max(0, Math.floor((cropTop - bounds.top) / cellHeight) - 1);
    const lastRow = Math.min(rows, Math.ceil((cropTop + cropHeight - bounds.top) / cellHeight) + 1);
    context.strokeStyle = 'rgba(230, 64, 54, .9)';
    context.lineWidth = 2.5;
    context.beginPath();
    for (let column = firstColumn; column <= lastColumn; column += 1) {
      const x = toCanvasX(bounds.left + column * cellWidth);
      context.moveTo(x, 0);
      context.lineTo(x, canvasHeight);
    }
    for (let row = firstRow; row <= lastRow; row += 1) {
      const y = toCanvasY(bounds.top + row * cellHeight);
      context.moveTo(0, y);
      context.lineTo(canvasWidth, y);
    }
    context.stroke();

    const edgeLeft = toCanvasX(bounds.left);
    const edgeRight = toCanvasX(bounds.right);
    const edgeTop = toCanvasY(bounds.top);
    const edgeBottom = toCanvasY(bounds.bottom);
    context.strokeStyle = '#e64036';
    context.lineWidth = 4;
    context.strokeRect(edgeLeft, edgeTop, edgeRight - edgeLeft, edgeBottom - edgeTop);

    const magnifierContext = context;
    function drawEdgeCrosshair(x: number, y: number) {
      if (x < -18 || x > canvasWidth + 18 || y < -18 || y > canvasHeight + 18) return;
      magnifierContext.fillStyle = 'rgba(255, 255, 255, .5)';
      magnifierContext.strokeStyle = 'rgba(255, 255, 255, .95)';
      magnifierContext.lineWidth = 7;
      magnifierContext.beginPath();
      magnifierContext.arc(x, y, 14, 0, Math.PI * 2);
      magnifierContext.fill();
      magnifierContext.stroke();
      magnifierContext.strokeStyle = '#e64036';
      magnifierContext.lineWidth = 4;
      magnifierContext.stroke();
      magnifierContext.beginPath();
      magnifierContext.moveTo(x - 17, y);
      magnifierContext.lineTo(x - 6, y);
      magnifierContext.moveTo(x + 6, y);
      magnifierContext.lineTo(x + 17, y);
      magnifierContext.moveTo(x, y - 17);
      magnifierContext.lineTo(x, y - 6);
      magnifierContext.moveTo(x, y + 6);
      magnifierContext.lineTo(x, y + 17);
      magnifierContext.stroke();
    }
    drawEdgeCrosshair(edgeLeft, edgeTop);
    drawEdgeCrosshair(edgeRight, edgeTop);
    drawEdgeCrosshair(edgeLeft, edgeBottom);
    drawEdgeCrosshair(edgeRight, edgeBottom);

    const cursorX = toCanvasX(point.x);
    const cursorY = toCanvasY(point.y);
    context.strokeStyle = 'rgba(255, 255, 255, .95)';
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(cursorX - 20, cursorY);
    context.lineTo(cursorX - 6, cursorY);
    context.moveTo(cursorX + 6, cursorY);
    context.lineTo(cursorX + 20, cursorY);
    context.moveTo(cursorX, cursorY - 20);
    context.lineTo(cursorX, cursorY - 6);
    context.moveTo(cursorX, cursorY + 6);
    context.lineTo(cursorX, cursorY + 20);
    context.stroke();
    context.strokeStyle = '#e64036';
    context.lineWidth = 2;
    context.stroke();
    context.beginPath();
    context.arc(cursorX, cursorY, 5, 0, Math.PI * 2);
    context.stroke();
  }, [bounds, columns, image, point, rows]);

  return <div className="recognition-magnifier">
    <div><strong>局部放大</strong><span>{point ? `X ${(point.x * 100).toFixed(1)}% · Y ${(point.y * 100).toFixed(1)}%` : '等待鼠标'}</span></div>
    <div className="recognition-magnifier-screen">
      <canvas ref={canvasRef} aria-label="鼠标周围局部放大预览" />
      {!point && <span>将鼠标移入左侧预览区域</span>}
    </div>
    <small>固定最大倍率，同步显示原图、粗网格与选框边缘准星</small>
  </div>;
}

export function PatternRecognitionModal({ file, maxCanvasSize, shortcutColors, onCancel, onConfirm }: Props) {
  const previewRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ mode: DragMode; x: number; y: number; bounds: PatternGridBounds } | null>(null);
  const previewPanRef = useRef<{ pointerId: number; x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const erasureHistoryRef = useRef<Set<number>[]>([]);
  const erasureRedoRef = useRef<Set<number>[]>([]);
  const erasureStrokeRef = useRef<{ pointerId: number; before: Set<number>; changed: boolean; element: HTMLDivElement } | null>(null);
  const initializedRef = useRef(false);
  const [url, setUrl] = useState('');
  const [natural, setNatural] = useState({ width: 1, height: 1 });
  const [width, setWidth] = useState(48);
  const [height, setHeight] = useState(60);
  const [bounds, setBounds] = useState<PatternGridBounds>({ left: 0.03, top: 0.03, right: 0.97, bottom: 0.9 });
  const [draft, setDraft] = useState<PatternRecognitionDraft | null>(null);
  const [mappings, setMappings] = useState<Map<number, number>>(new Map());
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);
  const [recognizing, setRecognizing] = useState(false);
  const [error, setError] = useState('');
  const [previewView, setPreviewView] = useState<PreviewView>({ zoom: 100, x: 0, y: 0 });
  const [previewPanning, setPreviewPanning] = useState(false);
  const [sourceImage, setSourceImage] = useState<HTMLImageElement | null>(null);
  const [magnifierPoint, setMagnifierPoint] = useState<{ x: number; y: number } | null>(null);
  const [calibrationTool, setCalibrationTool] = useState<'align' | 'eraser'>('align');
  const [ignoredCells, setIgnoredCells] = useState<Set<number>>(new Set());
  const [erasureVersion, setErasureVersion] = useState(0);
  const [configuredShortcutColors, setConfiguredShortcutColors] = useState(shortcutColors);
  const [shortcutPaletteEditorOpen, setShortcutPaletteEditorOpen] = useState(false);
  const previewZoom = previewView.zoom;
  const canUndoErasure = erasureVersion >= 0 && erasureHistoryRef.current.length > 0;
  const canRedoErasure = erasureVersion >= 0 && erasureRedoRef.current.length > 0;

  useEffect(() => {
    const next = URL.createObjectURL(file);
    setSourceImage(null);
    setMagnifierPoint(null);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  useEffect(() => {
    if (natural.width <= 1 || initializedRef.current) return;
    initializedRef.current = true;
    const square = Math.abs(natural.width / natural.height - 1) < 0.1;
    const nextWidth = square ? Math.min(128, maxCanvasSize) : 48;
    const nextHeight = square ? Math.min(128, maxCanvasSize) : 60;
    setWidth(nextWidth);
    setHeight(nextHeight);
    setBounds(fitBounds(nextWidth, nextHeight, natural));
  }, [maxCanvasSize, natural]);

  const gridStyle = {
    left: `${bounds.left * 100}%`,
    top: `${bounds.top * 100}%`,
    width: `${(bounds.right - bounds.left) * 100}%`,
    height: `${(bounds.bottom - bounds.top) * 100}%`,
    '--cols': width,
    '--rows': height
  } as CSSProperties;
  const columnMarkers = useMemo(() => rulerMarkers(width), [width]);
  const rowMarkers = useMemo(() => rulerMarkers(height), [height]);
  const mappedBeads = useMemo(() => {
    if (!draft) return 0;
    return draft.groups.reduce((sum, group) => (mappings.get(group.id) ?? -1) >= 0 ? sum + group.count : sum, 0);
  }, [draft, mappings]);

  const activeGroup = useMemo(() => {
    if (!draft) return null;
    return draft.groups.find((group) => group.id === activeGroupId) || draft.groups[0] || null;
  }, [activeGroupId, draft]);
  const nearColors = useMemo(() => {
    if (!activeGroup) return [];
    return MARD_291.map((color, index) => ({
      color,
      index,
      distance: colorDistance(activeGroup.sampleHex, color.hex),
      shortcut: configuredShortcutColors.includes(index)
    })).sort((left, right) => Number(right.shortcut) - Number(left.shortcut) || left.distance - right.distance).slice(0, 18);
  }, [activeGroup, configuredShortcutColors]);

  function changePreviewZoom(next: number | ((current: number) => number), clientX?: number, clientY?: number) {
    const viewport = viewportRef.current;
    const rect = viewport?.getBoundingClientRect();
    const anchorX = rect ? (clientX ?? rect.left + rect.width / 2) - rect.left : 0;
    const anchorY = rect ? (clientY ?? rect.top + rect.height / 2) - rect.top : 0;
    setPreviewView((current) => {
      const requested = typeof next === 'function' ? next(current.zoom) : next;
      const normalized = Math.max(100, Math.min(400, Math.round(requested / 25) * 25));
      if (normalized === current.zoom) return current;
      const ratio = normalized / current.zoom;
      return {
        zoom: normalized,
        x: anchorX - (anchorX - current.x) * ratio,
        y: anchorY - (anchorY - current.y) * ratio
      };
    });
  }

  function zoomPreviewWithWheel(event: WheelEvent<HTMLDivElement>) {
    event.preventDefault();
    changePreviewZoom((current) => current + (event.deltaY < 0 ? 25 : -25), event.clientX, event.clientY);
  }

  function beginPreviewPan(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 1) return;
    event.preventDefault();
    previewPanRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      offsetX: previewView.x,
      offsetY: previewView.y
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setPreviewPanning(true);
  }

  function movePreviewPan(event: PointerEvent<HTMLDivElement>) {
    const pan = previewPanRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    setPreviewView((current) => ({
      ...current,
      x: pan.offsetX + event.clientX - pan.x,
      y: pan.offsetY + event.clientY - pan.y
    }));
  }

  function endPreviewPan(event: PointerEvent<HTMLDivElement>) {
    if (previewPanRef.current?.pointerId !== event.pointerId) return;
    previewPanRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setPreviewPanning(false);
  }

  function resetErasure() {
    setIgnoredCells(new Set());
    erasureHistoryRef.current = [];
    erasureRedoRef.current = [];
    erasureStrokeRef.current = null;
    setErasureVersion((current) => current + 1);
  }

  function changeGridDimension(axis: 'width' | 'height', value: number) {
    if ((axis === 'width' ? width : height) === value) return;
    resetErasure();
    if (axis === 'width') setWidth(value);
    else setHeight(value);
  }

  function gridCellAt(event: PointerEvent<HTMLDivElement>, element: HTMLDivElement) {
    const rect = element.getBoundingClientRect();
    const column = Math.floor((event.clientX - rect.left) / rect.width * width);
    const row = Math.floor((event.clientY - rect.top) / rect.height * height);
    if (column < 0 || column >= width || row < 0 || row >= height) return -1;
    return row * width + column;
  }

  function eraseAtPointer(event: PointerEvent<HTMLDivElement>) {
    const stroke = erasureStrokeRef.current;
    if (!stroke || stroke.pointerId !== event.pointerId) return;
    const cell = gridCellAt(event, stroke.element);
    if (cell < 0) return;
    setIgnoredCells((current) => {
      if (current.has(cell)) return current;
      stroke.changed = true;
      const next = new Set(current);
      next.add(cell);
      return next;
    });
  }

  function beginErasure(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    erasureStrokeRef.current = {
      pointerId: event.pointerId,
      before: new Set(ignoredCells),
      changed: false,
      element: event.currentTarget
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    eraseAtPointer(event);
  }

  function endErasure(event: PointerEvent<HTMLDivElement>) {
    const stroke = erasureStrokeRef.current;
    if (!stroke || stroke.pointerId !== event.pointerId) return;
    if (stroke.element.hasPointerCapture(event.pointerId)) stroke.element.releasePointerCapture(event.pointerId);
    if (stroke.changed) {
      erasureHistoryRef.current = [...erasureHistoryRef.current.slice(-29), stroke.before];
      erasureRedoRef.current = [];
      setErasureVersion((current) => current + 1);
    }
    erasureStrokeRef.current = null;
  }

  function undoErasure() {
    const previous = erasureHistoryRef.current.pop();
    if (!previous) return;
    erasureRedoRef.current.push(new Set(ignoredCells));
    setIgnoredCells(new Set(previous));
    setErasureVersion((current) => current + 1);
  }

  function redoErasure() {
    const next = erasureRedoRef.current.pop();
    if (!next) return;
    erasureHistoryRef.current.push(new Set(ignoredCells));
    setIgnoredCells(new Set(next));
    setErasureVersion((current) => current + 1);
  }

  function beginDrag(event: PointerEvent, mode: DragMode) {
    if (event.button !== 0) return;
    event.preventDefault();
    dragRef.current = { mode, x: event.clientX, y: event.clientY, bounds };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveDrag(event: PointerEvent) {
    const drag = dragRef.current;
    const rect = previewRef.current?.getBoundingClientRect();
    if (!drag || !rect) return;
    const dx = (event.clientX - drag.x) / rect.width;
    const dy = (event.clientY - drag.y) / rect.height;
    const next = { ...drag.bounds };
    if (drag.mode === 'move') {
      const width = drag.bounds.right - drag.bounds.left;
      const height = drag.bounds.bottom - drag.bounds.top;
      next.left = clamp(drag.bounds.left + dx, 0, 1 - width);
      next.right = next.left + width;
      next.top = clamp(drag.bounds.top + dy, 0, 1 - height);
      next.bottom = next.top + height;
    } else {
      if (drag.mode.includes('n')) next.top = clamp(drag.bounds.top + dy, 0, drag.bounds.bottom - 0.04);
      if (drag.mode.includes('s')) next.bottom = clamp(drag.bounds.bottom + dy, drag.bounds.top + 0.04, 1);
      if (drag.mode.includes('w')) next.left = clamp(drag.bounds.left + dx, 0, drag.bounds.right - 0.04);
      if (drag.mode.includes('e')) next.right = clamp(drag.bounds.right + dx, drag.bounds.left + 0.04, 1);
    }
    setBounds(next);
  }

  function trackMagnifier(event: PointerEvent<HTMLDivElement>) {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMagnifierPoint({
      x: clamp((event.clientX - rect.left) / rect.width),
      y: clamp((event.clientY - rect.top) / rect.height)
    });
  }

  async function recognize() {
    setRecognizing(true);
    setError('');
    try {
      const next = maskRecognitionCells(await recognizePatternImage(file, { width, height, bounds }), ignoredCells);
      setDraft(next);
      setMappings(new Map(next.groups.map((group) => [group.id, group.paletteIndex])));
      setActiveGroupId(next.groups[0]?.id ?? null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '图纸识别失败');
    } finally {
      setRecognizing(false);
    }
  }

  function applyShortcutPalette() {
    if (!draft) return;
    if (!configuredShortcutColors.length) {
      setError('还没有快捷色卡，请先点击“设置快捷色卡”添加常用色号。');
      return;
    }
    setMappings(matchRecognitionGroupsToPalette(draft, configuredShortcutColors));
    setError('');
  }

  function finish() {
    if (!draft) return;
    onConfirm(materializeRecognition(draft, mappings), configuredShortcutColors);
  }

  function chooseNearColor(paletteIndex: number) {
    if (!activeGroup) return;
    setMappings((current) => {
      const next = new Map(current);
      next.set(activeGroup.id, paletteIndex);
      return next;
    });
  }

  useEffect(() => {
    if (draft) return;
    function handleCalibrationShortcut(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, select, textarea')) return;
      const key = /^Key[A-Z]$/.test(event.code) ? event.code.slice(3).toLowerCase() : event.key.toLowerCase();
      if (key === 'e') setCalibrationTool('eraser');
      else if (key === 'v') setCalibrationTool('align');
      else if (key === 'f') setPreviewView({ zoom: 100, x: 0, y: 0 });
      else if (key === 'z' && canUndoErasure) undoErasure();
      else if (key === 'y' && canRedoErasure) redoErasure();
      else return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    window.addEventListener('keydown', handleCalibrationShortcut, true);
    return () => window.removeEventListener('keydown', handleCalibrationShortcut, true);
  }, [canRedoErasure, canUndoErasure, draft, ignoredCells]);

  return (
    <div className="modal-backdrop recognition-backdrop" role="dialog" aria-modal="true" aria-labelledby="recognition-title">
      <div className="recognition-modal">
        <button className="icon-button modal-close" type="button" onClick={onCancel} aria-label="关闭图纸识别"><X size={18} /></button>
        <div className="recognition-heading">
          <div><p className="eyebrow">成品图纸导入</p><h2 id="recognition-title"><ScanLine size={22} />{draft ? '校对识别结果' : '对齐图纸网格'}</h2></div>
          <span><ShieldCheck size={15} />只在本机识别</span>
        </div>

        {!draft ? <div className="recognition-calibrate">
          <section className="recognition-preview-column">
            <p>让红色选框贴住图纸最外侧网格线。滚轮缩放预览，按住鼠标中键拖动画面，拖动选框或四角准星微调。</p>
            <div className="recognition-preview-tools">
              <span>预览缩放</span>
              <button type="button" onClick={() => changePreviewZoom(previewZoom - 25)} disabled={previewZoom <= 100} aria-label="缩小图纸预览"><ZoomOut size={16} /></button>
              <input type="range" min="100" max="400" step="25" value={previewZoom} onChange={(event) => changePreviewZoom(Number(event.target.value))} aria-label="图纸预览缩放比例" />
              <strong>{previewZoom}%</strong>
              <button type="button" onClick={() => changePreviewZoom(previewZoom + 25)} disabled={previewZoom >= 400} aria-label="放大图纸预览"><ZoomIn size={16} /></button>
              <button className="recognition-zoom-reset" type="button" onClick={() => setPreviewView({ zoom: 100, x: 0, y: 0 })} disabled={previewZoom === 100 && previewView.x === 0 && previewView.y === 0} title="适合窗口（F）" aria-keyshortcuts="F">适合窗口 <kbd>F</kbd></button>
            </div>
            <div className="recognition-grid-edit-tools">
              <div role="group" aria-label="识别前网格处理工具">
                <button className={calibrationTool === 'align' ? 'active' : ''} type="button" onClick={() => setCalibrationTool('align')} title="拖动或调整网格（V）" aria-keyshortcuts="V"><Move size={16} /><span>对齐</span><kbd>V</kbd></button>
                <button className={calibrationTool === 'eraser' ? 'active' : ''} type="button" onClick={() => setCalibrationTool('eraser')} title="擦除多余或错误色块（E）" aria-keyshortcuts="E"><Eraser size={16} /><span>橡皮</span><kbd>E</kbd></button>
              </div>
              <span className="recognition-erasure-status">{ignoredCells.size ? `已擦除 ${ignoredCells.size} 格` : '可先擦掉多余内容'}</span>
              <div role="group" aria-label="擦除历史">
                <button type="button" onClick={undoErasure} disabled={!canUndoErasure} title="撤销擦除（Z）" aria-label="撤销擦除" aria-keyshortcuts="Z"><Undo2 size={16} /></button>
                <button type="button" onClick={redoErasure} disabled={!canRedoErasure} title="重做擦除（Y）" aria-label="重做擦除" aria-keyshortcuts="Y"><Redo2 size={16} /></button>
              </div>
            </div>
            <div
              ref={viewportRef}
              className={`recognition-source-viewport${previewPanning ? ' panning' : ''}`}
              onWheel={zoomPreviewWithWheel}
              onPointerDown={beginPreviewPan}
              onPointerMove={movePreviewPan}
              onPointerUp={(event) => { endPreviewPan(event); dragRef.current = null; }}
              onPointerCancel={(event) => { endPreviewPan(event); dragRef.current = null; }}
              onAuxClick={(event) => event.preventDefault()}
            >
              <div
                ref={previewRef}
                className="recognition-source-preview"
                style={{
                  aspectRatio: `${natural.width} / ${natural.height}`,
                  transform: `translate3d(${previewView.x}px, ${previewView.y}px, 0) scale(${previewZoom / 100})`
                }}
                onPointerEnter={trackMagnifier}
                onPointerMove={(event) => { if (calibrationTool === 'eraser') eraseAtPointer(event); else moveDrag(event); trackMagnifier(event); }}
                onPointerLeave={() => setMagnifierPoint(null)}
                onPointerUp={(event) => { endErasure(event); dragRef.current = null; }}
                onPointerCancel={(event) => { endErasure(event); dragRef.current = null; }}
              >
                {url && <img src={url} alt="待识别的成品图纸" draggable={false} onLoad={(event) => { setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setSourceImage(event.currentTarget); }} />}
                <span className="recognition-grid-mask top" style={{ height: `${bounds.top * 100}%` }} />
                <span className="recognition-grid-mask bottom" style={{ top: `${bounds.bottom * 100}%` }} />
                <span className="recognition-grid-mask left" style={{ top: `${bounds.top * 100}%`, width: `${bounds.left * 100}%`, height: `${(bounds.bottom - bounds.top) * 100}%` }} />
                <span className="recognition-grid-mask right" style={{ top: `${bounds.top * 100}%`, left: `${bounds.right * 100}%`, height: `${(bounds.bottom - bounds.top) * 100}%` }} />
                <div className={`recognition-grid-box${calibrationTool === 'eraser' ? ' erasing' : ''}`} style={gridStyle} onPointerDown={(event) => calibrationTool === 'eraser' ? beginErasure(event) : beginDrag(event, 'move')}>
                  <div className="recognition-erasure-layer" aria-hidden="true">
                    {[...ignoredCells].map((cell) => <span key={cell} style={{ gridColumn: cell % width + 1, gridRow: Math.floor(cell / width) + 1 }} />)}
                  </div>
                  <span className="grid-size-badge">宽 {width} × 高 {height}</span>
                  <span className="grid-origin-badge">1</span>
                  {columnMarkers.map((value) => <span key={`col-${value}`} className="grid-ruler-label column" style={{ left: `${value / width * 100}%` }}>{value}</span>)}
                  {rowMarkers.map((value) => <span key={`row-${value}`} className="grid-ruler-label row" style={{ top: `${value / height * 100}%` }}>{value}</span>)}
                  {calibrationTool === 'align' && (['nw', 'ne', 'sw', 'se'] as DragMode[]).map((mode) => <button key={mode} className={`grid-handle ${mode}`} type="button" aria-label={`调整网格${mode}角`} onPointerDown={(event) => { if (event.button === 0) event.stopPropagation(); beginDrag(event, mode); }} />)}
                </div>
              </div>
            </div>
          </section>
          <aside className="recognition-settings">
            <div className="recognition-file"><ImagePlus size={18} /><span><strong>{file.name}</strong><small>{natural.width} × {natural.height} 像素</small></span></div>
            <fieldset>
              <legend>图纸尺寸（格）</legend>
              <div className="dimension-row">
                <DimensionInput id="recognition-width" label="宽" value={width} max={maxCanvasSize} onCommit={(value) => changeGridDimension('width', value)} />
                <span>×</span>
                <DimensionInput id="recognition-height" label="高" value={height} max={maxCanvasSize} onCommit={(value) => changeGridDimension('height', value)} />
              </div>
              <button className="fit-grid-button" type="button" onClick={() => setBounds(fitBounds(width, height, natural))}><Maximize2 size={15} />按宽高重新适配选框</button>
            </fieldset>
            <fieldset>
              <legend>色号品牌</legend>
              <div className="recognition-brand"><strong>MARD</strong><span>MARD 291 色<small>已接入识别</small></span></div>
            </fieldset>
            <div className="recognition-tip"><Grid3X3 size={17} /><span><strong>不要框入坐标和底部色卡</strong><small>选框边缘应压在第一条和最后一条网格线上。</small></span></div>
            {error && <p className="recognition-error" role="alert">{error}</p>}
            <button className="primary-button recognition-next" type="button" disabled={recognizing} onClick={() => { void recognize(); }}>
              {recognizing ? <LoaderCircle className="spin" size={19} /> : <WandSparkles size={19} />}{recognizing ? '正在识别网格…' : '识别并进入色号校对'}
            </button>
            <CalibrationMagnifier image={sourceImage} bounds={bounds} columns={width} rows={height} point={magnifierPoint} />
          </aside>
        </div> : <div className="recognition-review">
          <section className="recognition-result-card">
            <DraftPreview draft={draft} mappings={mappings} />
            <div><strong>{draft.width} × {draft.height}</strong><span>{mappedBeads} 颗 · {draft.groups.length} 个颜色组 · 本地 {draft.elapsedMs}ms</span></div>
            <p>透明区域表示识别为空白，修改右侧色号会立即更新预览。</p>
          </section>
          <section className="recognition-mapping-panel">
            <div className="recognition-shortcut-palette">
              <div><SwatchBook size={18} /><span><strong>当前图纸快捷色卡</strong><small>先配置常用色号，再按快捷色卡重新匹配。</small></span></div>
              <div className="recognition-shortcut-colors">
                {configuredShortcutColors.length ? configuredShortcutColors.slice(0, 18).map((index) => <i key={index} style={{ background: MARD_291[index]?.hex }} title={`MARD ${MARD_291[index]?.code}`}>{MARD_291[index]?.code}</i>) : <span>尚未设置快捷色卡</span>}
              </div>
              <div className="recognition-shortcut-actions">
                <button type="button" onClick={() => setShortcutPaletteEditorOpen(true)}>{configuredShortcutColors.length ? <>编辑<br />快捷色卡</> : <>设置<br />快捷色卡</>}</button>
                <button type="button" onClick={applyShortcutPalette} disabled={!configuredShortcutColors.length}>按快捷色卡<br />重新匹配</button>
              </div>
            </div>
            <div className="mapping-head"><div><strong>近似色肉眼校准模式</strong><span>先选择识别颜色组，再从右侧接近色块中确认；预览会立即更新。</span></div></div>
            <div className="recognition-near-layout">
              <div className="recognition-source-groups">
                {draft.groups.map((group) => {
                  const paletteIndex = mappings.get(group.id) ?? -1;
                  const target = paletteIndex >= 0 ? MARD_291[paletteIndex] : null;
                  return <button className={activeGroup?.id === group.id ? 'active' : ''} key={group.id} type="button" onClick={() => setActiveGroupId(group.id)}>
                    <span className="source-swatch" style={{ background: group.sampleHex, color: readableTextColor(group.sampleHex) }}>{target?.code || '空'}</span>
                    <span><strong>{group.count} 颗</strong><small>{target ? `MARD ${target.code}` : '已设为空白'}</small></span>
                    <em>{Math.round(group.confidence * 100)}%</em>
                  </button>;
                })}
              </div>
              <div className="recognition-near-colors">
                <div className="recognition-near-title">
                  <span className="source-swatch" style={{ background: activeGroup?.sampleHex, color: readableTextColor(activeGroup?.sampleHex || '#fff') }}>{activeGroup ? MARD_291[mappings.get(activeGroup.id) ?? -1]?.code || '空' : '-'}</span>
                  <span><strong>与原图取样色接近的色块</strong><small>快捷色卡中的颜色会优先排列并显示“快捷”。</small></span>
                </div>
                <div className="recognition-near-grid">
                  <button className={(activeGroup && (mappings.get(activeGroup.id) ?? -1) < 0) ? 'active empty' : 'empty'} type="button" onClick={() => chooseNearColor(-1)}><i /><span><strong>空白</strong><small>忽略杂色格</small></span>{activeGroup && (mappings.get(activeGroup.id) ?? -1) < 0 && <Check size={15} />}</button>
                  {nearColors.map(({ color, index, shortcut }) => <button className={activeGroup && mappings.get(activeGroup.id) === index ? 'active' : ''} type="button" key={color.code} onClick={() => chooseNearColor(index)}>
                    <i style={{ background: color.hex }} />
                    <span><strong>{color.code}</strong><small>{shortcut ? '快捷色卡' : '接近色'}</small></span>
                    {activeGroup && mappings.get(activeGroup.id) === index && <Check size={15} />}
                  </button>)}
                </div>
              </div>
            </div>
            {error && <p className="recognition-error" role="alert">{error}</p>}
          </section>
          <div className="recognition-review-actions">
            <button className="secondary-button" type="button" onClick={() => { setDraft(null); setError(''); }}><ArrowLeft size={17} />返回调整网格</button>
            <button className="primary-button" type="button" disabled={!mappedBeads} onClick={finish}><Check size={18} />导入并开始单色施工</button>
          </div>
        </div>}
      </div>
      {shortcutPaletteEditorOpen && <ShortcutPaletteModal
        selectedColor={activeGroup ? mappings.get(activeGroup.id) ?? null : null}
        colors={configuredShortcutColors}
        onChange={setConfiguredShortcutColors}
        onSelect={(index) => {
          chooseNearColor(index);
          setShortcutPaletteEditorOpen(false);
        }}
        onClose={() => setShortcutPaletteEditorOpen(false)}
      />}
    </div>
  );
}
